import { AggregationPeriod, MerchantLoanActivityMetric, METRIC_KEYS } from '@src/types/MerchantStatistic'
import { useMemo } from 'react'

const aggregateMetrics = (
  stats: MerchantLoanActivityMetric[],
  getKey: (date: Date) => string,
  getDisplayDate: (key: string) => string,
): MerchantLoanActivityMetric[] => {
  const aggregationMap = new Map<string, MerchantLoanActivityMetric>()

  stats.forEach((stat) => {
    const date = new Date(stat.date)
    const key = getKey(date)

    if (!aggregationMap.has(key)) {
      aggregationMap.set(key, {
        date: getDisplayDate(key),
        merchantId: stat.merchantId,
        totalApplications: 0,
        totalApplicationsAmount: 0,
        totalCreatedLoans: 0,
        totalGrantedAmount: 0,
        totalApprovedApplications: 0,
        totalMaxApprovalAmount: 0,
        totalPromoApplicationsWorksheet: 0,
        totalPromoApprovedWorksheetsAmountAfterMdr: 0,
      })
    }

    const aggregated = aggregationMap.get(key)!
    METRIC_KEYS.forEach((metricKey) => {
      aggregated[metricKey] += stat[metricKey] ?? 0
    })
  })

  return Array.from(aggregationMap.values()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
}

export const useComputeLoanActivityMetricsAggregation = (
  merchantLoanActivityMetrics: MerchantLoanActivityMetric[],
  aggregation: AggregationPeriod,
): MerchantLoanActivityMetric[] => {
  return useMemo(() => {
    if (!merchantLoanActivityMetrics.length) return []

    switch (aggregation) {
      case AggregationPeriod.Daily:
        return merchantLoanActivityMetrics

      case AggregationPeriod.Weekly:
        return aggregateMetrics(
          merchantLoanActivityMetrics,
          (date) => {
            const weekStart = new Date(date)
            weekStart.setDate(date.getDate() - date.getDay())
            return weekStart.toISOString().split('T')[0]
          },
          (key) => key,
        )

      case AggregationPeriod.Monthly:
        return aggregateMetrics(
          merchantLoanActivityMetrics,
          (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`,
          (key) => `${key}-01`,
        )

      default:
        return merchantLoanActivityMetrics
    }
  }, [merchantLoanActivityMetrics, aggregation])
}
